/**
 * Clock abstraction for deterministic testing in Edge Functions
 * 
 * Usage:
 * - Production: Use clock.now() instead of new Date() or Date.now()
 * - Tests: Set FIXED_TIME environment variable to freeze time
 * 
 * Example:
 *   export FIXED_TIME="2025-01-15T14:30:00.000Z"
 */

class Clock {
  private fixedTime: Date | null = null;

  constructor() {
    // DTC-I: defensive Deno access. The edge clock is now transitively
    // imported by Node-environment unit tests via migrated `_shared/`
    // helpers, where `Deno` is undefined. Guard the env-var read so
    // module construction doesn't crash outside the Edge runtime.
    let fixedTimeStr: string | undefined;
    try {
      fixedTimeStr = typeof Deno !== 'undefined' ? Deno.env.get('FIXED_TIME') : undefined;
    } catch {
      fixedTimeStr = undefined;
    }
    if (fixedTimeStr) {
      try {
        this.fixedTime = new Date(fixedTimeStr);
        console.log(`[Clock] Fixed time mode enabled: ${this.fixedTime.toISOString()}`);
      } catch (error) {
        console.error(`[Clock] Invalid FIXED_TIME format: ${fixedTimeStr}`);
      }
    }
  }

  /**
   * Get current time (or fixed time in tests)
   */
  now(): Date {
    return this.fixedTime ? new Date(this.fixedTime) : new Date();
  }

  /**
   * Get current timestamp in milliseconds
   */
  nowMs(): number {
    return this.now().getTime();
  }

  /**
   * Set a fixed time for testing (pass null to restore real time)
   */
  setFixed(date: Date | null): void {
    this.fixedTime = date;
  }

  /**
   * Check if clock is in test mode
   */
  isFixed(): boolean {
    return this.fixedTime !== null;
  }

  /**
   * Advance fixed time by milliseconds (only works in test mode)
   */
  advance(ms: number): void {
    if (!this.fixedTime) {
      throw new Error('Cannot advance time - clock is not in fixed mode');
    }
    this.fixedTime = new Date(this.fixedTime.getTime() + ms);
  }
}

export const clock = new Clock();
